import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ContextualMenu} from '@Components/contextual-menu';
import {AnimationSpeed, AnimationType, SLIDESHOW_INTRO_ANIMATIONS} from '@PosterWhiteboard/classes/item-animation.class';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {getSvgFromType, getTextFromType} from '@Components/poster-editor/components/poster-editing-side-panel/components/intro-animation-list-item/intro-animation-svgs';
import {useUpdateEffect} from 'ahooks';
import {SliderTextOptions} from '@Components/slider-text-options';
import {BlockAnimationColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/block-animation-color-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {ContextualMenuHorizontalScrollingSection} from '@Components/contextual-menu/components/contextual-menu-horizontal-scrolling-section';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetNumberOfSlidesForActiveSlideshowItem} from '@Hooks/poster-editor/useGetNumberOfSlidesForActiveSlideshowItem';
import {useGetKeyFromTransitionOfActiveItem} from '@Hooks/poster-editor/useGetKeyFromTransitionOfActiveItem';
import styles from './poster-editor-slideshow-transition-panel.module.scss';

interface PosterEditorSlideshowTransitionPanelProps {
  panelId: string;
}

enum PanelToShow {
  DEFAULT = 'DEFAULT',
  COLOR_PICKER = 'COLOR_PICKER',
}

export default function PosterEditorSlideshowTransitionPanel({...props}: PosterEditorSlideshowTransitionPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const transitionType = useGetKeyFromTransitionOfActiveItem('type');
  const transitionSpeed = useGetKeyFromTransitionOfActiveItem('speed');
  const transitionColor = useGetKeyFromTransitionOfActiveItem('color');
  const [panelToShow, setPanelToShow] = useState(PanelToShow.DEFAULT);
  const numOfSlides = useGetNumberOfSlidesForActiveSlideshowItem();

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useEffect((): void => {
    if (activeItem?.isSlideshow() && transitionType !== undefined) {
      updateHeightInRedux(transitionType);
    }
  }, [activeSlideUID, activeItemId, panelToShow]);

  const updateHeightInRedux = (type: AnimationType): void => {
    if (!activeItem?.isSlideshow()) {
      return;
    }

    let height = 210;

    if (type === AnimationType.NONE || !isTransitionApplicable()) {
      updateOptionsPanelHeight(props.panelId, `${height}px`);
      return;
    }

    height += 84;

    if (activeSlide?.isMediaSlide() && type === AnimationType.BLOCK) {
      height += 32 + 16;
    }

    updateOptionsPanelHeight(props.panelId, `${height}px`);
  };

  if (!activeItem) {
    return null;
  }

  if (!activeItem.isSlideshow() || !transitionColor || transitionSpeed === undefined) {
    return null;
  }

  const isTransitionApplicable = (): boolean => {
    return numOfSlides !== 1;
  };

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_transition')} />;
  };

  const onTransitionModified = (id: AnimationType): void => {
    if (transitionType === id) {
      return;
    }
    void activeItem.updateFromObject(
      {
        transition: {
          type: id,
        },
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );

    updateHeightInRedux(id);
  };

  const getTextFromSpeedType = (type: AnimationSpeed): string => {
    switch (type) {
      case AnimationSpeed.LOWER:
        return window.i18next.t('pmwjs_slower');
      case AnimationSpeed.LOW:
        return window.i18next.t('pmwjs_slow');
      case AnimationSpeed.MEDIUM:
        return window.i18next.t('pmwjs_medium');
      case AnimationSpeed.HIGH:
        return window.i18next.t('pmwjs_fast');
      case AnimationSpeed.HIGHER:
        return window.i18next.t('pmwjs_faster');
      default:
        throw new Error('unhandled speed type');
    }
  };

  const getSpeedDataArray = (): string[] => {
    return [AnimationSpeed.LOWER, AnimationSpeed.LOW, AnimationSpeed.MEDIUM, AnimationSpeed.HIGH, AnimationSpeed.HIGHER];
  };

  const onToggleSpeed = (id: AnimationSpeed, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        transition: {
          speed: id,
        },
      },
      {undoable, checkForDurationUpdate: undoable, replayPosterOnUpdateDone: undoable}
    );
  };

  const getSpeedOption = (): ReactElement | null => {
    if (transitionType === AnimationType.NONE) {
      return null;
    }
    return (
      <SliderTextOptions
        data={getSpeedDataArray()}
        label={window.i18next.t('pmwjs_speed')}
        value={getTextFromSpeedType(transitionSpeed)}
        selectedId={transitionSpeed}
        onChange={(newType: AnimationSpeed): void => {
          onToggleSpeed(newType, false);
        }}
        onChangeEnded={(newType: AnimationSpeed): void => {
          onToggleSpeed(newType, true);
        }}
      />
    );
  };

  const getSubOptions = (): ReactElement | null => {
    if (!activeItem.hasTransition() && !isTransitionApplicable()) {
      return null;
    }

    return (
      <div className="spacing-p-l-4 spacing-p-r-4 spacing-m-b-4">
        <BlockAnimationColorOption
          className="spacing-m-b-4"
          onClickColorFill={(): void => {
            setPanelToShow(PanelToShow.COLOR_PICKER);
          }}
        />
        {getSpeedOption()}
      </div>
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="slideshow-transition-options-default" includeLeftRightPadding={false} hasMobileMorphingProperties contentClassName="spacing-p-t-1 spacing-p-b-4">
        {getSubOptions()}
        <ContextualMenuHorizontalScrollingSection className={`spacing-m-0 flex-h-row spacing-p-l-4 spacing-p-r-4 ${styles.list}`}>
          {SLIDESHOW_INTRO_ANIMATIONS.map((item: AnimationType) => {
            return (
              <ContextualMenuThumbnailItem
                text={getTextFromType(item)}
                key={item}
                className={`intro-animation-svg-container flex-v-row ${isTransitionApplicable() ? '' : '-disabled'} ${styles.listItem} ${transitionType === item ? '-selected' : ''}`}
                thumbnail={getSvgFromType(item)}
                isSelected={transitionType === item && isTransitionApplicable()}
                selectionState={ThumbnailItemSelectionState.BORDER}
                onClick={(): void => {
                  if (!isTransitionApplicable()) {
                    return;
                  }
                  onTransitionModified(item);
                }}
              />
            );
          })}
        </ContextualMenuHorizontalScrollingSection>
      </ContextualMenu>
    );
  };

  const onChangeColor = (newColor: RGBA[], undoable = true): void => {
    void activeItem.updateFromObject(
      {
        transition: {
          color: newColor[0],
        },
      },
      {undoable}
    );
  };

  const getColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillType: FillTypes.SOLID,
          fillColor: [transitionColor],
        }}
        onChangeColor={onChangeColor}
        onClose={onClose}
        onBackButton={(): void => {
          setPanelToShow(PanelToShow.DEFAULT);
        }}
      />
    );
  };

  const getPanels = (): ReactElement => {
    switch (panelToShow) {
      case PanelToShow.COLOR_PICKER:
        return getColorPanel();
      case PanelToShow.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  return getPanels();
}
